import React, {useContext} from "react";
import {Card, Typography, Box} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {useNavigate} from "react-router-dom";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {LocaleContext} from "@/contexts/localeContext";
import {palette} from "@/constants";
import CourseChip from "./CourseChip";

interface CustomCourseCardProps {
	title: string;
	contentType?: string;
	userEnrollmentType?: string;
	authorCohort?: {
		title: string;
	};
	localizations?: Array<{
		title?: string;
		description?: string;
	}>;
	medium: string;
	slug?: string;
	index?: number;
	percentCompleted?: number;
	minutes?: number;
	userProgressPercentage?: number;
	totalDurationSeconds?: number;
	watchedDurationSeconds?: number;
	remainingDurationSeconds?: number;
	courseProgress?: number;
}

const useStyles = makeStyles()((theme) => ({
	card: {
		width: 368,
		height: 500,
		flex: "0 0 auto",
		borderRadius: 22,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		boxShadow: theme.shadows[2],
		margin: 4,
		position: "relative",
		cursor: "pointer",
		transition: "all 0.3s ease",

		"&:hover": {
			transform: "translateY(-4px)",
			boxShadow: theme.shadows[4],
			"& .hover-orange-overlay": {
				opacity: 1
			}
		},
		// Mobile responsive - maintain proportions
		[theme.breakpoints.down("lg")]: {
			width: 340,
			height: 462
		},
		[theme.breakpoints.down("md")]: {
			width: 320,
			height: 435
		},
		[theme.breakpoints.down("sm")]: {
			width: 300,
			height: 408
		},
		[theme.breakpoints.down("xs")]: {
			width: 280,
			height: 380
		}
	},
	cardSuccess: {
		border: `1px solid ${palette.success}`,
		"&:hover": {
			border: `1px solid ${palette.success}`
		}
	},
	cardDefault: {
		border: "1px solid transparent",
		"&:hover": {
			border: `1px solid ${palette.orange}`
		}
	},
	cardEnrolled: {
		border: `1px solid ${palette.orange}`
	},
	backgroundImage: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		zIndex: 1
	},
	image: {
		width: "100%",
		height: "100%",
		objectFit: "cover"
	},
	hoverOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "calc(100% - 195px)",
		zIndex: 2,
		transition: "all 0.3s ease",
		"&:hover": {
			background: `linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
			linear-gradient(0deg, ${palette.orange}20, ${palette.orange}20)`
		},
		// Mobile responsive - maintain proportions
		[theme.breakpoints.down("lg")]: {
			height: "calc(100% - 180px)"
		},
		[theme.breakpoints.down("md")]: {
			height: "calc(100% - 170px)"
		},
		[theme.breakpoints.down("sm")]: {
			height: "calc(100% - 160px)"
		},
		[theme.breakpoints.down("xs")]: {
			height: "calc(100% - 148px)"
		}
	},
	hoverOrangeOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		zIndex: 2,
		backgroundColor: `${palette.orange}1A`, // Orange with 10% opacity
		pointerEvents: "none",
		opacity: 0,
		transition: "opacity 0.3s ease"
	},
	contentOverlay: {
		position: "relative",
		zIndex: 3,
		width: "100%",
		height: "100%",
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-end",
		paddingBottom: "20px",
		paddingLeft: "16px",
		paddingRight: "16px",
		boxSizing: "border-box"
	},
	textContainer: {
		backgroundColor: palette.greyDark,
		borderRadius: "16px",
		width: "100%",
		height: "150px",
		padding: "12px",
		opacity: 1,
		boxSizing: "border-box",
		display: "flex",
		flexDirection: "column",
		// Mobile responsive - increase height to prevent clipping
		"@media (max-width: 1200px)": {
			height: "145px",
			padding: "11px"
		},
		"@media (max-width: 1024px)": {
			height: "150px",
			padding: "10px"
		},
		"@media (max-width: 900px)": {
			height: "145px",
			padding: "9px"
		},
		"@media (max-width: 768px)": {
			height: "140px",
			padding: "8px"
		},
		[theme.breakpoints.down("sm")]: {
			height: "135px",
			padding: "7px"
		}
	},
	contentContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		padding: "0px 0px 0px",
		gap: "4px",
		width: "100%",
		height: "118px",
		flex: "none",
		order: 0,
		alignSelf: "stretch",
		flexGrow: 0,
		justifyContent: "flex-start",
		// Mobile responsive - increase height to prevent clipping
		"@media (max-width: 1024px)": {
			height: "130px",
			gap: "3px"
		},
		"@media (max-width: 900px)": {
			height: "125px",
			gap: "3px"
		},
		"@media (max-width: 768px)": {
			height: "120px",
			gap: "2px"
		},
		[theme.breakpoints.down("sm")]: {
			height: "115px",
			gap: "2px"
		}
	},
	chipSection: {
		height: "32px",
		display: "flex",
		alignItems: "center",
		// Mobile responsive - maintain proportions
		[theme.breakpoints.down("sm")]: {
			height: "24px"
		}
	},
	titleSection: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-start",
		alignItems: "stretch",
		height: "52px", // Reduced height for 2 lines
		// Mobile responsive - more generous height to prevent clipping
		"@media (max-width: 768px)": {
			height: "52px"
		},
		[theme.breakpoints.down("sm")]: {
			height: "50px" // More space for two lines on mobile
		}
	},
	categorySection: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "flex-end",
		alignItems: "stretch",
		minHeight: "16px",
		flex: "0 0 auto"
	},
	bottomSection: {
		height: "20px",
		display: "flex",
		alignItems: "center",
		// Mobile responsive - maintain proportions
		[theme.breakpoints.down("sm")]: {
			height: "16px"
		}
	},
	title: {
		fontFamily: "Titillium Web",
		fontWeight: 600,
		fontSize: "20px",
		lineHeight: "1.3",
		letterSpacing: "0.15px",
		whiteSpace: "normal",
		wordBreak: "break-word",
		overflowWrap: "break-word",
		color: palette.white,
		display: "-webkit-box",
		WebkitLineClamp: 2,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		textOverflow: "ellipsis",
		width: "100%",
		textAlign: "left",
		alignSelf: "flex-start",
		// Remove maxHeight constraints to use full title section space
		// Mobile responsive - prevent font clipping
		"@media (max-width: 768px)": {
			fontSize: "18px",
			lineHeight: "1.35"
		},
		[theme.breakpoints.down("sm")]: {
			fontSize: "18px",
			lineHeight: "1.35"
		}
	},
	contentType: {
		fontFamily: "Titillium Web",
		fontWeight: 400,
		fontSize: "14px",
		lineHeight: "120%",
		letterSpacing: "0.17px",
		verticalAlign: "middle",
		whiteSpace: "normal",
		wordBreak: "break-word",
		overflowWrap: "break-word",
		color: "rgba(255, 255, 255, 0.7)",
		display: "-webkit-box",
		WebkitLineClamp: 1,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		textOverflow: "ellipsis",
		// Mobile responsive - maintain proportions
		"@media (max-width: 768px)": {
			fontSize: "13px"
		},
		"@media (max-width: 480px)": {
			fontSize: "12px"
		}
	},
	assignmentInfo: {
		color: palette.white
	},
	chipContainer: {
		display: "flex",
		gap: "8px",
		marginBottom: "12px",
		minHeight: "24px"
	},
	progressChip: {
		backgroundColor: palette.orange,
		color: palette.white,
		fontSize: "12px",
		height: "24px"
	},
	successChip: {
		backgroundColor: palette.success,
		color: palette.white,
		fontSize: "12px",
		height: "24px"
	},
	timeChip: {
		backgroundColor: palette.grey15,
		color: palette.white,
		fontSize: "12px",
		height: "24px"
	},
	authorCohort: {
		fontFamily: "Titillium Web",
		fontWeight: 500,
		fontSize: "13px",
		lineHeight: "120%",
		letterSpacing: "0.17px",
		color: "rgba(255, 255, 255, 0.8)",
		display: "-webkit-box",
		WebkitLineClamp: 1,
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		textOverflow: "ellipsis",
		marginBottom: "2px",
		// Mobile responsive - maintain proportions
		"@media (max-width: 768px)": {
			fontSize: "12px"
		},
		"@media (max-width: 480px)": {
			fontSize: "11px"
		}
	}
}));

const CustomCourseCard: React.FC<CustomCourseCardProps> = ({
	title,
	contentType,
	userEnrollmentType,
	authorCohort,
	localizations,
	medium,
	slug,
	index,
	percentCompleted,
	minutes,
	userProgressPercentage,
	totalDurationSeconds,
	watchedDurationSeconds,
	remainingDurationSeconds,
	courseProgress
}) => {
	const classes = useStyles();
	const navigate = useNavigate();
	const {t} = useSSTranslation();
	const [localeState] = useContext(LocaleContext);
	const isSpanish = localeState.locale === "es" || localeState.locale === "es-419";

	// Use localizations based on user language preferences
	// If user prefers Spanish and localizations exist, use Spanish title
	// Otherwise, always fall back to English title
	const displayTitle = isSpanish && localizations && localizations.length > 0 && localizations[0]?.title
		? localizations[0].title
		: title;

	// Format author cohort title - if it begins with "BuildWitt", only display "BuildWitt"
	const getAuthorCohortDisplayName = () => {
		if (!authorCohort?.title) return null;
		return authorCohort.title.startsWith("BuildWitt") ? "BuildWitt" : authorCohort.title;
	};

	// Determine chip type based on progress percentage from API
	const getChipTypeFromProgress = () => {
		// Use new API field if available, otherwise fall back to percentCompleted
		const progress = userProgressPercentage !== undefined ? userProgressPercentage : percentCompleted;

		if (progress === undefined || progress === null) {
			return "empty";
		}
		if (progress === 100 || courseProgress === 100) {
			return "success";
		}
		if (progress > 0) {
			return "warning";
		}
		return "empty";
	};

	// Determine if course is completed using new API fields
	const isCourseCompleted = () => {
		// Use new API logic: user enrolled, no remaining time, and has watched content
		if (userEnrollmentType && ((remainingDurationSeconds === 0 && watchedDurationSeconds && watchedDurationSeconds > 0) || courseProgress === 100)) {
			return true;
		}

		// Fallback to progress percentage if new fields not available
		const progress = userProgressPercentage !== undefined ? userProgressPercentage : percentCompleted;
		return progress === 100;
	};

	const isSuccess = isCourseCompleted();
	const isEnrolled = !!userEnrollmentType;

	const getCardBorderClass = () => {
		if (isSuccess) return classes.classes.cardSuccess;
		if (isEnrolled) return classes.classes.cardEnrolled;
		return classes.classes.cardDefault;
	};

	const cardClasses = `${classes.classes.card} ${getCardBorderClass()}`;
	const chipType = getChipTypeFromProgress();

	const handleClick = () => {
		if (slug) {
			// Check if user is enrolled in the course
			const isEnrolled = !!userEnrollmentType;

			if (isEnrolled) {
				// User is enrolled, go to course page
				navigate(`/course/${slug}`);
			} else {
				// User is not enrolled, go to course library overview
				navigate(`/course-library/${slug}/overview`);
			}
		}
	};

	return (
		<Card
			className={cardClasses}
			onClick={handleClick}
			aria-label={`${displayTitle} course card${chipType !== "empty" ? ` - ${chipType === "success" ? "Completed" : `${percentCompleted}%`} progress` : ""}`}
			data-test-id={index ? `course-card-${String(index).padStart(2, "0")}` : undefined}
		>
			{/* Background image covering entire card */}
			<Box
				className={classes.classes.backgroundImage}
				style={{
					background: `linear-gradient(359deg, rgba(0, 0, 0, 0.00) 58.34%, rgba(0, 0, 0, 0.50) 99.32%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), url(${medium}) lightgray 50% / cover no-repeat`
				}}
			/>

			{/* Hover overlay - only over image area */}
			<Box className={classes.classes.hoverOverlay} />

			{/* Hover orange overlay - only for non-completed cards */}
			{!isSuccess && (
				<Box className={`${classes.classes.hoverOrangeOverlay} hover-orange-overlay`} />
			)}

			{/* Content overlay */}
			<Box className={classes.classes.contentOverlay}>
				{/* Black container for text */}
				<Box className={classes.classes.textContainer}>
					{/* Content Container */}
					<Box className={classes.classes.contentContainer}>
						{/* Top Section - Chips */}
						<Box className={classes.classes.chipSection}>
							{/* Progress and Time Chip using CourseChip component */}
							<CourseChip
								progressPercentage={userProgressPercentage}
								watchedDurationSeconds={watchedDurationSeconds}
								remainingDurationSeconds={remainingDurationSeconds}
								minutes={minutes}
								userEnrollmentType={userEnrollmentType}
								totalDurationSeconds={totalDurationSeconds}
								courseProgress={courseProgress}
							/>
						</Box>

						{/* Middle Section - Title */}
						<Box className={classes.classes.titleSection}>
							<Typography
								variant="h6"
								component="div"
								align="left"
								data-test-id={index ? `course-card-title-${String(index).padStart(2, "0")}` : undefined}
								className={classes.classes.title}
							>
								{displayTitle}
							</Typography>
						</Box>

						{/* Bottom Section - Author Cohort and Category */}
						{(getAuthorCohortDisplayName() || contentType || userEnrollmentType) && (
							<Box className={classes.classes.categorySection}>
								{getAuthorCohortDisplayName() && (
									<Typography
										variant="body2"
										component="div"
										align="left"
										data-test-id={index ? `course-card-author-cohort-${String(index).padStart(2, "0")}` : undefined}
										className={classes.classes.authorCohort}
									>
										{getAuthorCohortDisplayName()}
									</Typography>
								)}
								{(contentType || userEnrollmentType) && (
									<Typography
										variant="body2"
										component="div"
										align="left"
										data-test-id={index ? `course-card-content-type-${String(index).padStart(2, "0")}` : undefined}
										className={classes.classes.contentType}
									>
										{contentType && t(`course_category_${contentType}`, contentType)}
										{contentType && userEnrollmentType && " • "}
										{userEnrollmentType && t(`user_enrollment_type_${userEnrollmentType}`, userEnrollmentType)}
									</Typography>
								)}
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		</Card>
	);
};

export default CustomCourseCard;